import {
  canManageCustomers,
  customerView,
  findCustomerByCode,
  normalizeCustomerCode,
} from '@parkingless/interface';
import { Garage } from '../interface/garage';
import { Scan } from './barcode';

/** The stay a scanned ticket asks to end, on the way to Sosta oraria. */
export const STOP_TICKET_PARAM = 'sosta';
export const STOP_PLATE_PARAM = 'targa';

/** The code "Aggiungi cliente" opens with, from a card that belongs to nobody yet. */
export const NEW_CUSTOMER_CODE_PARAM = 'codice';

/** Where a scan leads: a page to open, or something to say when there is none. */
export type ScanDestination =
  | {
      readonly commands: readonly string[];
      readonly queryParams?: Readonly<Record<string, string>>;
    }
  | { readonly toast: string };

/**
 * Where a scan goes when the page on display has no use of its own for it.
 *
 * - a stay ticket: Sosta oraria, which ends that stay;
 * - a customer's card: the customer's own page. The previous console went to the
 *   month's receipts instead; the page lists all of them with the same actions, and an
 *   employee, who is refused Ricevute, can open it;
 * - a card nobody has: Aggiungi cliente with the code filled in — for those who may add
 *   customers; an employee is told the card is unknown.
 */
export function scanDestination(
  scan: Scan,
  garage: Garage,
  role: string | null | undefined,
): ScanDestination | null {
  const base = ['/parking', garage.garageId];

  if (scan.kind === 'hourly') {
    return {
      commands: [...base, 'hourly-parking'],
      queryParams: { [STOP_TICKET_PARAM]: scan.ticketId, [STOP_PLATE_PARAM]: scan.plate },
    };
  }

  const code = normalizeCustomerCode(scan.code);
  if (scan.kind !== 'customer' || code === '') {
    return null;
  }

  const customer = findCustomerByCode(garage.customerList, code);
  if (customer) {
    return { commands: [...base, 'customer-registry', customer.customerId] };
  }

  return canManageCustomers(customerView(role))
    ? { commands: [...base, 'add-customer'], queryParams: { [NEW_CUSTOMER_CODE_PARAM]: code } }
    : { toast: `Nessun cliente con il codice ${code}` };
}
